import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

/**
 * The one moment the register is asked by itself: arriving on a record that was just
 * saved (TRKC-79). A form that saves and lands on the record stamps the navigation
 * with this; the record's screen reads it once and clears it.
 *
 * Why a navigation stamp and not "the record has numbers and no answer": only a
 * Verified answer is filed, so NotFound, Ambiguous and Unavailable leave the record
 * looking never-asked — and asking again on every visit was exactly the per-page-load
 * cost 1.3 refused, spent hardest during an outage of the register (review finding on
 * #15). A visit is a visit; a save is the event.
 */
export const JUST_SAVED = { justSaved: true } as const

export function wasJustSaved(state: unknown): boolean {
  return typeof state === 'object' && state !== null && (state as { justSaved?: unknown }).justSaved === true
}

/**
 * Whether this screen was reached by saving the record it shows. The stamp is read
 * and then cleared from history, so a reload of the same page is an ordinary visit
 * again; the answer is remembered for as long as the screen stays on that path.
 *
 * Keyed on the location rather than read once at mount, because the broker form is
 * ONE component for `/brokers/new` and `/brokers/:id`: registering navigates from the
 * first to the second without remounting it, and a mount-time read would have missed
 * the arrival entirely (the portal test caught exactly that).
 */
export function useArrivedFromSave(): boolean {
  const location = useLocation()
  const navigate = useNavigate()
  const [arrivedAt, setArrivedAt] = useState<string | null>(() =>
    wasJustSaved(location.state) ? location.pathname : null,
  )

  useEffect(() => {
    if (!wasJustSaved(location.state)) {
      return
    }

    // Remembered before the stamp goes: the panel that reads this mounts later, once
    // the record has loaded, and must still find the answer.
    setArrivedAt(location.pathname)
    void navigate(location.pathname, { replace: true, state: null })
  }, [location, navigate])

  return arrivedAt === location.pathname
}
